/**
 * Project line items — the install manifest (EX.4). List, bulk-persist (from
 * intake, after the project is created), and crew check-off.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProjectLineItem,
  ProjectLineItemCreateInput,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

type ListResponse = { data: ProjectLineItem[] };

function key(projectId: string) {
  return ['projects', projectId, 'line-items'] as const;
}

export function useProjectLineItems(projectId: string | undefined) {
  return useQuery<ListResponse>({
    queryKey: key(projectId ?? ''),
    enabled: !!projectId,
    queryFn: () => apiCall<ListResponse>(`/projects/${projectId}/line-items`),
  });
}

/** Bulk-insert a project's manifest (intake flow, post-create). Best-effort. */
export async function persistProjectLineItems(
  projectId: string,
  items: ProjectLineItemCreateInput[],
): Promise<void> {
  if (items.length === 0) return;
  await apiCall(`/projects/${projectId}/line-items`, {
    method: 'POST',
    body: { items },
  });
}

export function useToggleLineItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    ProjectLineItem,
    Error,
    { id: string; received?: boolean; installed?: boolean }
  >({
    mutationFn: ({ id, received, installed }) =>
      apiCall<ProjectLineItem>(`/line-items/${id}`, {
        method: 'PATCH',
        body: {
          ...(received !== undefined ? { received } : {}),
          ...(installed !== undefined ? { installed } : {}),
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(projectId) }),
  });
}
